/**
 * Mode PROJECTION. Baseline = modèle AMÉLIORÉ (Elo + valeur d'effectif). Pour
 * l'équipe choisie : parcours probable + adversaires possibles par tour (MC).
 *
 * WHAT-IF en WIZARD, tour par tour : à chaque étape, les groupes qui pèsent sur
 * un tour (placement, puis adversaires en 16es / 8es…). Les matchs à venir sont
 * PRÉ-REMPLIS par le score prédit du modèle (et par le score LIVE si en cours),
 * MODIFIABLES. Le classement projeté du groupe (J/Pts/Diff/BP) reflète ces scores ;
 * les règles de départage CdM 2026 sont rappelées. « Recalculer » relance le MC
 * conditionné sur les résultats joués + en direct + tes modifications.
 */
import { McClient, defaultSimCount } from "../app/mcClient.ts";
import type { FixedResult, SimulationOutput } from "../engine/montecarlo.ts";
import type { EloTable } from "../engine/types.ts";
import { track } from "../app/track.ts";
import {
  teams,
  groups,
  schedule,
  hosts,
  poissonParams,
  modelHomeAdvantage,
  shootoutModel,
  gkRatings,
  gkDivisor,
} from "../app/data.ts";
import { lambdas } from "../engine/poisson.ts";
import { rankGroup } from "../engine/standings.ts";
import { valueBlendedElos, VALUE_BLEND_WEIGHT } from "../app/valueModel.ts";
import { teamFormRatings, ensureTeamRatings, applyFormFactor, applyAbsenceFactor } from "../app/teamRatings.ts";
import { ensureInjuries, absencePenalties } from "../app/injuryData.ts";
import {
  opponentsByRound,
  likeliestMatchByRound,
  teamSideAt,
  nextBracketNode,
  nodeOpponents,
  r32SlotsFeeding,
  teamPositionPaths,
  nodePathFrom,
  type OpponentChance,
} from "../app/projection.ts";
import { esc, flagSpan, flagCode, teamName, frontZ } from "./html.ts";
import { fmtPct, fmtKick } from "../app/format.ts";
import { t } from "../app/i18n.ts";
import seedValuesDoc from "../data/squad-values.json";

interface Base {
  elos: Record<string, number>;
  fixed: Record<number, FixedResult>;
  live: Record<number, readonly [number, number]>;
  finishedCount: number;
}
type ScheduleMatch = (typeof schedule)[number];

/** Une combinaison de résultats (matchs de groupe restants) réalisant un placement. */
interface CondScenario {
  readonly desc: string; // « Pays-Bas bat Tunisie · Japon bat Suède »
  readonly tiedWith: string | null; // code du rival À ÉGALITÉ DE POINTS (départage) ou null
  readonly h2h: "win" | "draw" | "loss" | null; // confrontation directe équipe vs rival (1ᵉʳ départage)
  readonly gdGap: number | null; // différence de buts ACTUELLE de l'équipe MOINS celle du rival
}
/** Résolution du placement d'une équipe dans son groupe (pour la modale conditions). */
interface PlacementSolve {
  readonly team: string;
  readonly place: number; // 1 / 2 / 3
  readonly group: string;
  readonly settled: boolean; // groupe terminé → placement déjà figé
  readonly settledOk: boolean; // si terminé : l'équipe EST-elle à cette place ?
  readonly scenarios: readonly CondScenario[];
  readonly tooMany: boolean; // trop de matchs restants pour énumérer
}

const byCode = new Map(teams.map((t) => [t.id, t]));
// Matchs KO indexés par id (nœud « M73 » → match 73) → stade + horaire d'un créneau.
const koMatchById = new Map(schedule.filter((m) => m.stage !== "group").map((m) => [m.id, m]));
// Étiquettes de créneau par match KO (« 2A »/« 1F »/« 3:ABCDF »/« W74 ») → conditions.
const slotByMatch = new Map(
  schedule.filter((m) => m.stage !== "group").map((m) => [m.id, { home: m.home, away: m.away }]),
);
const placeLabels = (): string[] => ["", t("proj.place.1"), t("proj.place.2"), t("proj.place.3")];
/** Place (1/2/3) encodée par une étiquette de créneau de groupe ; null pour « W… ». */
const placementOf = (slot: string | undefined): number | null => {
  const c = slot?.[0];
  return c === "1" ? 1 : c === "2" ? 2 : c === "3" ? 3 : null;
};
/** Place (1/2/3) qu'une équipe du groupe `group` doit prendre pour occuper le créneau
 *  `slot` : « 1B » → 1ᵉʳ de B, « 2B » → 2ᵉ, « 3:ABCD » (incluant B) → 3ᵉ (meilleur 3ᵉ) ;
 *  null si ce créneau ne peut pas être pris par ce groupe. */
const placementForGroup = (slot: string, group: string): number | null => {
  if (slot === `1${group}`) return 1;
  if (slot === `2${group}`) return 2;
  if (slot.startsWith("3") && slot.includes(group)) return 3;
  return null;
};
const roundLabels = (): string[] => [t("proj.round.r32"), t("proj.round.r16"), t("proj.round.qf"), t("proj.round.sf"), t("proj.round.final")];
/** % d'un adversaire : « < 1 % » pour une proba RÉELLE mais qui s'arrondirait à 0 (sinon
 *  une pastille « 0 % » laisse croire à tort que c'est impossible). */
const fmtOppPct = (p: number): string => (p > 0 && Math.round(p * 100) < 1 ? t("proj.lessThan1pct") : fmtPct(p, 0));
const shortLabels = (): Record<string, string> => ({ r32: t("proj.short.r32"), r16: t("proj.short.r16"), qf: t("proj.short.qf"), sf: t("proj.short.sf"), final: t("proj.round.final") });
// Facteur FORME (expérimental) : Elo += clamp(K·(note récente − moyenne), ±CAP).
// PLAFONNÉ pour ne jamais dominer l'Elo+valeur (σ Elo ≈ 173 → ±40 ≈ ±0,23σ).
const FORM_K = 60; // points d'Elo par point de note d'écart à la moyenne
const FORM_CAP = 40; // borne dure de l'ajustement
// Pénalité ABSENTS (expérimental) : Elo −= min(CAP, K·poids) — poids = Σ absences
// (« Missing Fixture » = 1, « Questionable » = 0,5). PLAFONNÉE comme la forme.
const ABSENCE_K = 12; // points d'Elo retranchés par unité de poids d'absence
const ABSENCE_CAP = 40; // borne dure (≈ 3+ titulaires absents = pénalité max)

let squadValues: Record<string, number> = (seedValuesDoc as { values: Record<string, number> }).values ?? {};
let valuesPromise: Promise<void> | null = null;
/** Charge les valeurs d'effectif fraîches UNE fois ; renvoie la promesse mise en
 *  cache. `simulate()` l'attend pour calculer d'emblée sur les bonnes valeurs
 *  (sinon le 1er calcul tourne sur la graine puis « saute » à l'interaction suivante). */
function loadValues(): Promise<void> {
  valuesPromise ??= fetch("/data/squad-values.json", { cache: "no-store" })
    .then((r) => (r.ok ? (r.json() as Promise<{ values?: Record<string, number> }>) : null))
    .then((d) => {
      if (d?.values && Object.keys(d.values).length >= 30) squadValues = d.values;
    })
    .catch(() => {});
  return valuesPromise;
}

const parseScore = (pair: readonly [string, string] | undefined): readonly [number, number] | null => {
  if (!pair) return null;
  const [hs, as] = pair;
  if (hs === "" || as === "") return null;
  const h = Number(hs);
  const a = Number(as);
  if (!Number.isInteger(h) || !Number.isInteger(a) || h < 0 || a < 0 || h > 30 || a > 30) return null;
  return [h, a];
};

export class Projection {
  private readonly mc = new McClient();
  private readonly nSims = defaultSimCount(); // figé une fois (pas de bascule 20k↔10k au resize)
  private readonly getBase: () => Base;
  private readonly el: HTMLElement;
  private output: SimulationOutput | null = null;
  private team = "";
  private wizardStep = 0; // tour courant (0-based)
  private modalEl: HTMLElement | null = null; // overlay de saisie (null = fermé)
  private roundDone = false; // le tour courant a été « Terminé »
  private everOpened = false; // le halo du bouton clignote tant qu'on ne l'a pas ouvert
  private formOn = true; // facteur forme (expérimental) appliqué aux Elo du modèle
  private absenceOn = true; // pénalité d'absents (blessés/suspendus, expérimental)
  private readonly scores = new Map<number, [string, string]>(); // overrides utilisateur
  private tabSel: Record<number, string> = {}; // adversaire choisi par tour (onglets) ; défaut = le plus probable
  private condModal: HTMLElement | null = null; // modale « conditions détaillées » (null = fermée)
  private running = false;
  private recalcTimer: ReturnType<typeof setTimeout> | null = null;
  private reqSeq = 0; // séquence des recalculs dynamiques (latest-wins côté UI)
  private lastMcKey = ""; // empreinte des entrées du dernier MC : recalcul SEULEMENT si ça change

  constructor(container: HTMLElement, options: { getBase: () => Base }) {
    this.el = container;
    this.getBase = options.getBase;
    container.innerHTML = `<div class="prj-msg note">${t("proj.opening")}</div>`;
    // Onglets de l'arbre — délégué sur le conteneur (persiste aux re-rendus). Choisir un
    // adversaire à un tour fixe le créneau et REPROJETTE les tours suivants ; l'état (tabSel)
    // survit aux recalculs Monte-Carlo. Le bouton « détails » ouvre la modale des conditions.
    container.addEventListener("click", (e) => {
      const el = e.target instanceof Element ? e.target : null;
      const tab = el?.closest<HTMLElement>("[data-tab]");
      if (tab?.dataset.tab) {
        const [d, code] = tab.dataset.tab.split(":");
        const depth = Number(d);
        this.tabSel[depth] = code ?? "";
        for (const k of Object.keys(this.tabSel)) if (Number(k) > depth) delete this.tabSel[Number(k)];
        const main = this.el.querySelector<HTMLElement>(".prj-main");
        if (main) main.innerHTML = this.mainHtml();
        return;
      }
      const det = el?.closest<HTMLElement>("[data-cond]");
      if (det?.dataset.cond) this.openCondModal(det.dataset.cond);
    });
  }

  activate(): void {
    void loadValues();
    this.closeModalNow(); // nettoie un overlay orphelin au retour sur l'onglet
    const base = this.getBase();
    // équipe par défaut : la PREMIÈRE par ordre alphabétique (même tri que le sélecteur) ;
    // repli sur la mieux notée si la liste est vide.
    if (!this.team)
      this.team = [...teams].sort((a, b) => a.nameFr.localeCompare(b.nameFr))[0]?.id ?? this.defaultTeam(base);
    if (!this.output || this.mcKey(base) !== this.lastMcKey) void this.run();
    else this.render();
  }

  /**
   * Appelé quand l'état officiel (résultats live) change. La projection ne lisait
   * `getBase()` qu'au moment de `activate()` : si on RECHARGE sur l'onglet
   * Projection, elle calculait sur l'état VIDE initial (aucun résultat → modèle
   * pur, ex. Maroc ~80%) et restait figée quand les vrais résultats arrivaient
   * (qui donnent ~100% si le Maroc est qualifié). On recalcule donc sur les
   * données fraîches dès que le nombre de matchs terminés bouge (et si visible).
   */
  onBaseUpdated(): void {
    if (this.el.classList.contains("hide")) return; // pas visible : MAJ à la prochaine activation
    if (this.mcKey(this.getBase()) === this.lastMcKey) return; // ni but, ni fin de match → pas de recalcul
    if (this.modalEl) void this.runQuiet(); // modale ouverte : MAJ ciblée (ne pas la détruire)
    else void this.run();
  }

  private modelElos(base: Base): EloTable {
    const blended = valueBlendedElos(squadValues, VALUE_BLEND_WEIGHT, base.elos);
    const form = this.formOn ? teamFormRatings() : null;
    const withForm = form ? applyFormFactor(blended, form, FORM_K, FORM_CAP) : blended;
    if (!this.absenceOn) return withForm;
    const pen = absencePenalties();
    return pen ? applyAbsenceFactor(withForm, pen, ABSENCE_K, ABSENCE_CAP) : withForm;
  }

  private defaultTeam(base: Base): string {
    const elos = this.modelElos(base);
    let best = teams[0]?.id ?? "FRA";
    let bestElo = -Infinity;
    for (const t of teams) {
      // une seule provenance : la table modèle (Elo+valeur). Pas de repli sur
      // t.elo (Elo base/legacy) — on ne compare jamais deux échelles (règle 3).
      const e = elos[t.id];
      if (e === undefined) continue;
      if (e > bestElo) {
        bestElo = e;
        best = t.id;
      }
    }
    return best;
  }

  /** Score PRÉDIT par le modèle (buts attendus arrondis) pour un match. */
  private predictedScore(m: ScheduleMatch, elos: EloTable): [number, number] {
    const eff = (code: string): number =>
      (elos[code] ?? 0) + (m.hostCountry != null && hosts[code] === m.hostCountry ? modelHomeAdvantage : 0);
    const [lh, la] = lambdas(eff(m.home), eff(m.away), poissonParams);
    return [Math.round(lh), Math.round(la)];
  }

  /** Score retenu pour le CLASSEMENT projeté : joué → réel ; modifié → override ;
   *  en direct → live ; sinon → prédiction du modèle. Toujours défini. */
  private scenarioScore(m: ScheduleMatch, base: Base, elos: EloTable): readonly [number, number] {
    const fr = base.fixed[m.id];
    if (fr) return fr.score;
    const ov = parseScore(this.scores.get(m.id));
    if (ov) return ov;
    const liveSc = base.live[m.id];
    if (liveSc) return liveSc;
    return this.predictedScore(m, elos);
  }

  private activeCount(): number {
    let n = 0;
    for (const p of this.scores.values()) if (parseScore(p)) n++;
    return n;
  }

  /** Résultats figés pour le MC : joués + EN DIRECT + tes modifications (les
   *  prédictions non modifiées restent SIMULÉES → les probas gardent l'incertitude). */
  private fixedForMc(base: Base): Record<number, FixedResult> {
    const fixed: Record<number, FixedResult> = { ...base.fixed };
    for (const [id, sc] of Object.entries(base.live)) {
      const n = Number(id);
      // un KO en cours encore à ÉGALITÉ ne doit pas être figé sans vainqueur TAB,
      // sinon playKoMatch retombe déterministe sur le slot domicile au lieu du
      // 50/50 acté. On le laisse re-simuler ; groupes (id ≤ 72) et KO déjà
      // décidés (score live non nul) sont figés.
      if (n <= 72 || sc[0] !== sc[1]) fixed[n] = { score: sc };
    }
    for (const [id, pair] of this.scores) {
      const sc = parseScore(pair);
      if (sc) fixed[id] = { score: sc };
    }
    return fixed;
  }

  /** Empreinte des ENTRÉES du Monte Carlo : résultats figés (matchs joués + scores
   *  en direct + saisies what-if) + nb de matchs finis. On ne relance le MC QUE si
   *  cette empreinte change → recalcul sur un VRAI événement (un but qui bouge un
   *  score live, une fin de match, une saisie), jamais « pour rien » (tick de poll,
   *  ouverture/fermeture de modale sans modification). */
  private mcKey(base: Base): string {
    const f = this.fixedForMc(base);
    const parts: string[] = [];
    for (const id of Object.keys(f).map(Number).sort((a, b) => a - b)) {
      const r = f[id];
      if (r) parts.push(`${id}:${r.score[0]}-${r.score[1]}${r.penaltiesWinner ?? ""}`);
    }
    // empreinte des absences : un /data/injuries.json rafraîchi DOIT relancer le MC.
    const pen = this.absenceOn ? absencePenalties() : null;
    const absHash = pen
      ? Object.entries(pen)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([c, w]) => `${c}${w}`)
          .join("")
      : "";
    return `${base.finishedCount};${this.formOn ? "F" : ""};${this.absenceOn ? "A" : ""}${absHash};${parts.join(",")}`;
  }

  /** Renvoie true si une NOUVELLE sortie a été calculée ; false si les entrées sont
   *  inchangées (rien recalculé) ou si le worker a été dépassé. */
  private async simulate(): Promise<boolean> {
    // valeurs d'effectif + index des notes (forme) prêts AVANT le calcul (pas de saut ensuite)
    await Promise.all([loadValues(), ensureTeamRatings(), ensureInjuries()]);
    const base = this.getBase();
    const key = this.mcKey(base);
    if (key === this.lastMcKey && this.output) return false; // entrées identiques → on garde le résultat
    try {
      const { output } = await this.mc.simulate({
        elos: this.modelElos(base),
        fixedResults: this.fixedForMc(base),
        nSims: this.nSims,
        // seed CONSTANT : deux états partagent le MÊME flux RNG (common random
        // numbers, renforcé par le RNG per-match du moteur). Les % ne bougent donc
        // QUE pour une vraie raison (résultat réel, saisie what-if) — plus de
        // jitter d'échantillonnage où le % « monte et descend » sans raison.
        seed: "wc26-proj",
        params: poissonParams,
        modelHomeAdvantage,
        shootoutModel,
        gkRatings,
        gkDivisor,
      });
      this.output = output;
      this.lastMcKey = key;
      return true;
    } catch {
      return false; // une simulation plus récente a pris la main
    }
  }

  /** Recalcul COMPLET (activate / reset) avec rendu complet. */
  private async run(): Promise<void> {
    this.running = true;
    this.render();
    await this.simulate();
    this.running = false;
    this.render();
  }

  /** Recalcul DYNAMIQUE après saisie d'un score : MAJ ciblée (projection +
   *  classements) SANS reconstruire le wizard → le focus de saisie est préservé. */
  private async runQuiet(): Promise<void> {
    const team = this.team;
    const seq = ++this.reqSeq;
    this.setStatus(true);
    const fresh = await this.simulate();
    // un recalcul/rendu plus récent (ou un changement d'équipe) a pris la main :
    // ne pas réécrire le DOM ni baisser le voile — la requête courante s'en charge.
    if (seq !== this.reqSeq || this.team !== team) return;
    if (fresh) {
      const main = this.el.querySelector<HTMLElement>(".prj-main");
      if (main) main.innerHTML = this.mainHtml();
      this.refreshModalStandings();
    }
    this.setStatus(false);
  }

  private clearRecalc(): void {
    if (this.recalcTimer) {
      clearTimeout(this.recalcTimer);
      this.recalcTimer = null;
    }
    this.reqSeq++; // invalide tout runQuiet en vol
  }

  private setStatus(on: boolean): void {
    this.el.querySelector(".prj-status")?.classList.toggle("hide", !on);
  }

  private mainHtml(): string {
    if (!this.output) return `<div class="prj-msg note">${this.running ? t("proj.computingMc") : t("proj.preparing")}</div>`;
    const title = this.output.titleProb[this.team] ?? 0;
    const head = `<div class="prj-head">${flagSpan(this.team, { name: true })}<span class="prj-champ">${t("proj.champion", { pct: fmtPct(title, 0) })}</span></div>`;
    return `${head}${this.pathHtml(this.output)}${this.treeHtml(this.output)}`;
  }

  // --- projection (haut) ----------------------------------------------------

  private pathHtml(output: SimulationOutput): string {
    const steps = [
      ...opponentsByRound(output, this.team).map((r) => ({ label: shortLabels()[r.round] ?? r.round, p: r.reach })),
      { label: t("proj.path.title"), p: output.titleProb[this.team] ?? 0 },
    ];
    return `<div class="prj-block-h">${t("proj.path.heading")}</div>
      <div class="prj-path">${steps
        .map(
          (s) => `<div class="prj-step"><div class="prj-bar"><span style="height:${Math.max(2, Math.round(s.p * 100))}%"></span></div>
          <div class="prj-step-p">${fmtPct(s.p, 0)}</div><div class="prj-step-l">${esc(s.label)}</div></div>`,
        )
        .join("")}</div>`;
  }

  /** « Stade · Ville · horaire » (fuseau visiteur) d'un match, ou "" si inconnu. */
  private static venueText(matchId: number | null): string {
    const m = matchId != null ? koMatchById.get(matchId) : null;
    if (!m) return "";
    return `${[m.stadium, m.city].filter(Boolean).join(" · ")} · ${fmtKick(m.utcDateTime)}`;
  }


  /** Conditions de qualification d'un affrontement à un nœud : placements de groupe pour
   *  les 16es ("Maroc 2ᵉ … et Pays-Bas 1ᵉʳ …") ; vide au-delà (créneaux « vainqueur de … »). */
  private conditionText(nodeId: string, teamSide: "a" | "b", oppCode: string): string {
    const slots = slotByMatch.get(Number(nodeId.slice(1)));
    if (!slots) return "";
    const tp = placementOf(teamSide === "a" ? slots.home : slots.away);
    const op = placementOf(teamSide === "a" ? slots.away : slots.home);
    // Au-delà des 16es, les créneaux sont « vainqueur de … » (pas de place de groupe) :
    // la condition pour cet affrontement = les deux se qualifient jusqu'à ce tour.
    if (!tp || !op) {
      return t("proj.cond.bothQualify", { team: esc(teamName(this.team)), opp: esc(teamName(oppCode)) });
    }
    const cond = (code: string, place: number): string => {
      const g = byCode.get(code)?.group;
      return `${esc(teamName(code))} ${placeLabels()[place]}${place === 3 ? t("proj.cond.bestThird") : ""}${g ? ` ${t("proj.cond.ofGroup", { group: g })}` : ""}`;
    };
    return t("proj.cond.if", { cond1: cond(this.team, tp), cond2: cond(oppCode, op) });
  }

  /** Matchs de groupe ENCORE À JOUER qui décideront les placements de la condition (le
   *  groupe de l'équipe + celui de l'adversaire). Vide si les groupes sont déjà finis. */
  private pendingText(base: Base, oppCode: string): string {
    const groups = [
      ...new Set([byCode.get(this.team)?.group, byCode.get(oppCode)?.group].filter(Boolean)),
    ] as string[];
    const parts = groups
      .map((g) => {
        const rem = schedule.filter(
          (m) => m.stage === "group" && m.group === g && !base.fixed[m.id],
        );
        return rem.length ? t("proj.pending.group", { group: g, matches: rem.map((m) => `${m.home}–${m.away}`).join(", ") }) : "";
      })
      .filter(Boolean);
    return parts.length ? t("proj.pending.decidedBy", { parts: parts.join(" · ") }) : "";
  }

  /** Le 16es, SEUL tour conditionné → en ONGLETS (pastilles côte à côte, trigramme sur
   *  mobile). La pastille active (défaut = la plus probable) affiche sa condition de
   *  groupe + matchs décisifs restants + stade, et « détails › » ouvre les combinaisons. */
  private r32BlockHtml(output: SimulationOutput, base: Base, opps: readonly OpponentChance[], sel: OpponentChance, depth: number): string {
    const chips = opps
      .map(
        (o) =>
          `<button type="button" class="prj-tab${o.code === sel.code ? " on" : ""}" data-tab="${depth}:${esc(o.code)}" title="${esc(teamName(o.code))}"><span class="f fi fi-${esc(flagCode(o.code))}" aria-hidden="true"></span><span class="prj-tab-n">${esc(teamName(o.code))}</span><span class="prj-tab-tri">${esc(o.code)}</span><b>${fmtOppPct(o.prob)}</b></button>`,
      )
      .join("");
    const selNode = sel.matchId != null ? `M${sel.matchId}` : null;
    const teamSide = selNode ? teamSideAt(output, this.team, selNode) : "a";
    const cond = selNode ? this.conditionText(selNode, teamSide, sel.code) : "";
    const pending = selNode ? this.pendingText(base, sel.code) : "";
    const venue = sel.matchId != null ? Projection.venueText(sel.matchId) : "";
    const details =
      sel.matchId != null
        ? `<button type="button" class="prj-detail-btn" data-cond="${depth}:${esc(sel.code)}:${sel.matchId}"><i class="ti ti-list-search" aria-hidden="true"></i> ${t("proj.r32.whichResults")}</button>`
        : "";
    return `<div class="prj-tround">${esc(roundLabels()[depth] ?? "")}</div>
      <div class="prj-tabs">${chips}</div>
      <div class="prj-tbody">
        ${cond ? `<div class="prj-tcond note">${cond}</div>` : ""}
        ${pending ? `<div class="prj-tpending note">${esc(pending)}</div>` : ""}
        ${venue ? `<div class="prj-venue note"><i class="ti ti-map-pin" aria-hidden="true"></i> ${esc(venue)}</div>` : ""}
        ${details}
      </div>`;
  }

  /** Un tour PROFOND (8es→finale) : une LIGNE par adversaire possible AU CRÉNEAU `node`
   *  (drapeau + nom + %), cliquable → conditions « pour qu'il se retrouve là ». Le créneau
   *  `node` découle de l'onglet 16es sélectionné (winnerTo) → stade/horaire COHÉRENTS avec
   *  la position que cet adversaire implique. */
  private roundLinesHtml(opps: readonly OpponentChance[], node: string, depth: number): string {
    const head = `<div class="prj-tround">${esc(roundLabels()[depth] ?? "")}</div>`;
    const mid = Number(node.slice(1));
    if (!opps.length) return `${head}<div class="prj-tnote note">${t("proj.path.undetermined")}</div>`;
    const venue = Projection.venueText(mid);
    const lines = opps
      .map(
        (o) =>
          `<button type="button" class="prj-line" data-cond="${depth}:${esc(o.code)}:${mid}" title="${t("proj.line.conditionsTitle", { opp: esc(teamName(o.code)) })}"><span class="f fi fi-${esc(flagCode(o.code))}" aria-hidden="true"></span><span class="prj-line-n">${esc(teamName(o.code))}</span><b class="prj-line-p">${fmtOppPct(o.prob)}</b><i class="ti ti-chevron-right prj-line-chev" aria-hidden="true"></i></button>`,
      )
      .join("");
    return `${head}
      ${venue ? `<div class="prj-venue note"><i class="ti ti-map-pin" aria-hidden="true"></i> ${esc(venue)}</div>` : ""}
      <div class="prj-lines">${lines}</div>`;
  }

  /** Panneau « vos villes changent selon votre place » : la place finale de l'équipe dans
   *  son groupe fixe son créneau R32, donc TOUT le parcours (villes des tours). Par place
   *  atteignable (proba MC), l'enchaînement de villes ; la plus probable mise en avant.
   *  Caché si la place est déjà figée (groupe terminé) → aucun impact à montrer. */
  private positionPanelHtml(output: SimulationOutput): string {
    const paths = teamPositionPaths(output, this.team, (id) => slotByMatch.get(id));
    if (paths.length < 2) return "";
    const group = byCode.get(this.team)?.group ?? "";
    const best = paths.reduce((a, b) => (b.prob > a.prob ? b : a));
    const rows = paths
      .map((p) => {
        const cities = nodePathFrom(p.r32Node)
          .map((n) => koMatchById.get(Number(n.slice(1)))?.city)
          .filter((c): c is string => !!c);
        const third = p.place === 3;
        return `<div class="prj-pp-row${p.place === best.place ? " best" : ""}">
          <span class="prj-pp-pos">${third ? t("proj.pos.bestThird") : placeLabels()[p.place] ?? ""}</span>
          <span class="prj-pp-prob">${fmtOppPct(p.prob)}</span>
          <span class="prj-pp-path">${cities.map((c) => esc(c)).join(" › ")}${third ? ` <span class="prj-pp-var">${t("proj.pos.variableSlot")}</span>` : ""}</span>
        </div>`;
      })
      .join("");
    return `<div class="prj-pp">
      <div class="prj-pp-h"><i class="ti ti-map-pin" aria-hidden="true"></i> ${t("proj.pos.heading", { group: esc(group) })}</div>
      ${rows}
    </div>`;
  }

  /** Arbre du parcours : le 16es en ONGLETS (seul tour conditionné), les tours suivants
   *  en LIGNES ; chaque ligne/onglet ouvre les conditions pour s'y retrouver. Précédé du
   *  panneau « villes selon la place » quand celle-ci n'est pas encore figée. */
  private treeHtml(output: SimulationOutput): string {
    const r32 = opponentsByRound(output, this.team).find((r) => r.round === "r32");
    if (!r32?.opponents.length) {
      return `<div class="prj-block-h">${t("proj.tree.heading")}</div><div class="prj-msg note">${t("proj.tree.undetermined")}</div>`;
    }
    const base = this.getBase();
    // L'onglet 16es sélectionné fixe le CRÉNEAU R32 de l'équipe → tout le reste du parcours
    // (stades/horaires + adversaires) en DÉCOULE par winnerTo. Choisir un adversaire d'un
    // autre créneau (donc d'une autre position de groupe) re-projette les tours suivants.
    const sel = (this.tabSel[0] && r32.opponents.find((o) => o.code === this.tabSel[0])) || r32.opponents[0];
    if (!sel) return `<div class="prj-block-h">${t("proj.tree.heading")}</div><div class="prj-msg note">${t("proj.tree.undetermined")}</div>`;
    let blocks = this.r32BlockHtml(output, base, r32.opponents, sel, 0);
    let node: string | null = sel.matchId != null ? `M${sel.matchId}` : null;
    let side: "a" | "b" = node ? teamSideAt(output, this.team, node) : "a";
    for (let depth = 1; depth < roundLabels().length; depth++) {
      const nx = node ? nextBracketNode(node) : null;
      if (!nx) break;
      node = nx.nodeId;
      side = nx.side;
      blocks += this.roundLinesHtml(nodeOpponents(output, this.team, node, side, 6), node, depth);
    }
    return `<div class="prj-block-h">${t("proj.tree.heading")} <span class="count">${t("proj.tree.subhead")}</span></div>
      ${this.positionPanelHtml(output)}
      <div class="prj-tree">${blocks}</div>`;
  }

  // --- wizard what-if (bas) -------------------------------------------------

  /** Étapes du wizard : groupes qui pèsent, regroupés PAR TOUR. */
  private wizardSteps(): { label: string; groups: string[] }[] {
    const tg = byCode.get(this.team)?.group;
    const feed = new Map<string, { idx: number; label: string }>();
    if (this.output) {
      opponentsByRound(this.output, this.team).forEach((r, idx) => {
        for (const o of r.opponents) {
          if (o.prob < 0.05) continue;
          const g = byCode.get(o.code)?.group;
          if (g && g !== tg && !feed.has(g)) feed.set(g, { idx, label: shortLabels()[r.round] ?? r.round });
        }
      });
    }
    const steps: { label: string; groups: string[] }[] = [];
    if (tg) steps.push({ label: t("proj.wizard.yourPlacement"), groups: [tg] });
    const byRound = new Map<string, string[]>();
    [...feed.entries()]
      .sort((a, b) => a[1].idx - b[1].idx || a[0].localeCompare(b[0]))
      .forEach(([g, info]) => {
        const key = info.label;
        (byRound.get(key) ?? byRound.set(key, []).get(key))?.push(g);
      });
    for (const [label, gs] of byRound) steps.push({ label: t("proj.wizard.opponentIn", { round: label }), groups: gs });
    return steps;
  }

  private standingsHtml(group: string, base: Base, elos: EloTable): string {
    const groupTeams = groups[group] ?? [];
    if (!groupTeams.length) return "";
    const results: { home: string; away: string; score: readonly [number, number] }[] = [];
    for (const m of schedule) {
      if (m.stage === "group" && m.group === group) results.push({ home: m.home, away: m.away, score: this.scenarioScore(m, base, elos) });
    }
    const ranked = rankGroup(groupTeams, results, { elos, drawSeed: `wc26-proj-${group}` });
    const rows = ranked
      .map(
        (r, i) => `<tr${r.team === this.team ? ' class="me"' : ""}><td class="prj-st-r">${i + 1}</td><td class="prj-st-t">${flagSpan(r.team)}</td><td>${r.played}</td><td class="prj-st-pts">${r.points}</td><td>${r.goalDiff > 0 ? "+" : ""}${r.goalDiff}</td><td>${r.goalsFor}</td></tr>`,
      )
      .join("");
    return `<table class="prj-standings"><thead><tr><th></th><th></th><th title="${t("proj.standings.playedTitle")}">${t("stats.col.played.abbr")}</th><th>${t("stats.col.points.abbr")}</th><th title="${t("proj.standings.gdTitle")}">${t("stats.col.diff.abbr")}</th><th title="${t("proj.standings.gfTitle")}">${t("stats.col.goalsFor.abbr")}</th></tr></thead><tbody>${rows}</tbody></table>`;
  }

  private groupCardHtml(group: string, label: string, base: Base, elos: EloTable): string {
    const cell = (code: string): string => `<span class="prj-wm-t">${flagSpan(code)}</span>`;
    const ms = schedule.filter((m) => m.stage === "group" && m.group === group).sort((a, b) => a.id - b.id);
    const matchRows = ms
      .map((m) => {
        const fr = base.fixed[m.id];
        if (fr) {
          const [h, a] = fr.score;
          return `<div class="prj-wm played" title="${t("proj.match.playedTitle")}">${cell(m.home)}<span class="prj-wm-sc">${h}–${a}</span>${cell(m.away)}</div>`;
        }
        const live = base.live[m.id];
        const sc = this.scenarioScore(m, base, elos);
        const inp = (side: "h" | "a", val: number): string =>
          `<input class="prj-sc" type="number" min="0" max="30" inputmode="numeric" aria-label="${side === "h" ? t("proj.match.homeScoreAria") : t("proj.match.awayScoreAria")}" data-mid="${m.id}" data-side="${side}" value="${val}">`;
        return `<div class="prj-wm${live ? " live" : ""}" data-mid="${m.id}"${live ? ` title="${t("proj.match.liveTitle")}"` : ""}>${cell(m.home)}<span class="prj-wm-score">${inp("h", sc[0])}<span class="prj-wm-dash">–</span>${inp("a", sc[1])}</span>${cell(m.away)}</div>`;
      })
      .join("");
    return `<div class="prj-gcard">
      <div class="prj-gc-h"><span class="prj-gc-round">${esc(label)}</span><span class="prj-gc-g">${t("stats.group.cell", { group: esc(group) })}</span></div>
      <div class="prj-gc-matches">${matchRows}</div>
      <div class="prj-standings-wrap" data-group="${group}">${this.standingsHtml(group, base, elos)}</div>
    </div>`;
  }

  /** Petit bouton d'appel (en haut, dans la barre de sélection) : halo qui clignote
   *  tant qu'on ne l'a pas ouvert, puis se transforme en « passer au prochain tour ». */
  private ctaHtml(): string {
    const steps = this.wizardSteps();
    if (!this.output || steps.length === 0) return "";
    const last = steps.length - 1;
    const i = Math.min(this.wizardStep, last);
    let label = t("proj.cta.project");
    let icon = "ti-edit";
    let cls = "";
    if (!this.everOpened) cls = " pulse";
    else if (this.roundDone && i < last) {
      label = t("proj.cta.nextRound");
      icon = "ti-arrow-right";
    } else if (this.roundDone && i >= last) {
      label = t("proj.cta.editScores");
      icon = "ti-edit";
      cls = " done";
    } else label = t("proj.cta.continue");
    return `<button type="button" class="prj-cta${cls}" aria-haspopup="dialog"><span class="prj-cta-dot" aria-hidden="true"></span><i class="ti ${icon}" aria-hidden="true"></i> ${label}</button>`;
  }

  /** Contenu de la modale de saisie pour LE tour courant (un tour à la fois). */
  private modalBodyHtml(base: Base, elos: EloTable): string {
    const steps = this.wizardSteps();
    const last = steps.length - 1;
    const i = Math.min(Math.max(0, this.wizardStep), last);
    const step = steps[i];
    if (!step) return "";
    const cards = step.groups.map((g) => this.groupCardHtml(g, step.label, base, elos)).join("");
    const dots = steps.map((_, k) => `<span class="prj-wz-dot${k === i ? " on" : k < i ? " done" : ""}"></span>`).join("");
    const n = this.activeCount();
    return `<div class="md-card prj-modal-card" role="dialog" aria-modal="true" aria-label="${t("proj.cta.project")} — ${esc(step.label)}">
      <button type="button" class="md-close" aria-label="${t("common.close")}"><i class="ti ti-x" aria-hidden="true"></i></button>
      <div class="md-body">
        <div class="prj-modal-h"><span class="prj-modal-step">${t("proj.modal.stepCount", { i: i + 1, n: steps.length })}</span><b>${esc(step.label)}</b><span class="prj-wz-dots">${dots}</span></div>
        <p class="prj-modal-hint note">${t("proj.modal.hint")}</p>
        <div class="prj-gcards">${cards}</div>
        <div class="prj-crit note"><b>${t("proj.modal.tiebreakTitle")}</b>${t("proj.modal.tiebreakBody")}</div>
        <div class="prj-modal-foot">
          <button type="button" class="prj-reset linklike${n ? "" : " hide"}">${n ? t("proj.modal.resetN", { n }) : t("proj.modal.reset")}</button>
          <button type="button" class="prj-done">${i < last ? t("proj.modal.finishRound") : t("proj.modal.finish")}</button>
        </div>
      </div>
    </div>`;
  }

  private render(): void {
    this.clearRecalc(); // un rendu complet annule tout recalcul dynamique en attente
    const opts = teams
      .slice()
      .sort((a, b) => a.nameFr.localeCompare(b.nameFr))
      .map((t) => `<option value="${t.id}"${t.id === this.team ? " selected" : ""}>${esc(t.nameFr)}</option>`)
      .join("");
    // Le bouton d'appel vit EN HAUT, dans la barre de sélection (intégré au décor).
    const formToggle = `<label class="prj-form" title="${t("proj.toggle.formTitle", { cap: FORM_CAP })}"><input type="checkbox" class="prj-form-cb"${this.formOn ? " checked" : ""}> ${t("proj.toggle.form")} <span class="prj-exp">${t("proj.toggle.exp")}</span></label>`;
    const absToggle = `<label class="prj-form" title="${t("proj.toggle.absTitle", { cap: ABSENCE_CAP })}"><input type="checkbox" class="prj-abs-cb"${this.absenceOn ? " checked" : ""}> ${t("proj.toggle.absences")} <span class="prj-exp">${t("proj.toggle.exp")}</span></label>`;
    const picker = `<div class="prj-pick"><label class="prj-pick-l" for="prjTeam">${t("proj.picker.label")}</label><select id="prjTeam" class="prj-team">${opts}</select><span class="prj-model note">${t("proj.picker.model")}</span>${formToggle}${absToggle}<span class="prj-status note hide">${t("proj.picker.recalc")}</span>${this.ctaHtml()}</div>`;
    this.el.innerHTML = `${picker}<div class="prj-main">${this.mainHtml()}</div>`;
    this.wire();
  }

  /** MAJ ciblée des classements DANS la modale (le focus de saisie est préservé). */
  private refreshModalStandings(): void {
    if (!this.modalEl) return;
    const base = this.getBase();
    const elos = this.modelElos(base);
    this.modalEl.querySelectorAll<HTMLElement>(".prj-standings-wrap").forEach((w) => {
      const g = w.dataset.group;
      if (g) w.innerHTML = this.standingsHtml(g, base, elos);
    });
    const n = this.activeCount();
    const reset = this.modalEl.querySelector<HTMLElement>(".prj-reset");
    if (reset) {
      reset.classList.toggle("hide", n === 0);
      reset.textContent = n ? t("proj.modal.resetN", { n }) : t("proj.modal.reset");
    }
  }

  private readonly onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") this.closeModal(false);
  };

  /** Ouvre la modale de saisie pour le tour courant. */
  private openModal(): void {
    this.closeModalNow();
    const base = this.getBase();
    const elos = this.modelElos(base);
    const ov = document.createElement("div");
    ov.className = "md-overlay prj-modal";
    ov.innerHTML = this.modalBodyHtml(base, elos);
    this.el.appendChild(ov);
    this.modalEl = ov;
    ov.style.zIndex = String(frontZ()); // au-dessus de toute modale déjà ouverte
    void ov.offsetWidth; // force le reflow → la transition d'entrée joue (indép. de rAF)
    ov.classList.add("open");
    track("projection_open");
    document.addEventListener("keydown", this.onKey);
    this.wireModal(ov);
  }

  /** Ferme avec animation. `done` = le tour a été validé (« Terminer »). */
  private closeModal(done: boolean): void {
    if (!this.modalEl) return;
    if (done) this.roundDone = true;
    const ov = this.modalEl;
    this.modalEl = null;
    document.removeEventListener("keydown", this.onKey);
    ov.classList.remove("open");
    setTimeout(() => ov.remove(), 300);
    this.refreshCta(); // bascule l'étiquette du bouton sans détruire l'overlay sortant
    void this.runQuiet(); // % à jour au retour
  }

  private closeModalNow(): void {
    if (!this.modalEl) return;
    document.removeEventListener("keydown", this.onKey);
    this.modalEl.remove();
    this.modalEl = null;
  }

  // --- modale CONDITIONS détaillées (#2) ------------------------------------
  // Résout, par énumération des matchs de groupe ENCORE À JOUER (victoire/nul/défaite),
  // TOUTES les combinaisons qui placent une équipe à une position donnée — d'où l'on tire
  // la condition exacte de l'affrontement (« la Suède 1ʳᵉ : Suède gagne ET Pays-Bas ne
  // gagne pas »), avec la marge de buts minimale quand l'égalité de points se tranche au
  // goal-average.

  private static outcomeDesc(home: string, away: string, tag: "H" | "D" | "A"): string {
    if (tag === "H") return t("proj.outcome.homeWins", { home: teamName(home), away: teamName(away) });
    if (tag === "A") return t("proj.outcome.awayWins", { away: teamName(away), home: teamName(home) });
    return t("proj.outcome.draw", { home: teamName(home), away: teamName(away) });
  }

  /** Rival À ÉGALITÉ DE POINTS avec `team` à la frontière de son rang (sinon null) : c'est
   *  LUI que le départage (confrontation directe, puis différence de buts, puis buts
   *  marqués) sépare — donc les scores qui touchent SON total impactent le classement. */
  private static tiedNeighbor(ranked: ReadonlyArray<{ team: string; points: number }>, idx: number): string | null {
    const me = ranked[idx];
    if (!me) return null;
    const above = ranked[idx - 1];
    const below = ranked[idx + 1];
    if (above && above.points === me.points) return above.team;
    if (below && below.points === me.points) return below.team;
    return null;
  }

  /** Toutes les combinaisons (V/N/D des matchs restants du groupe) qui placent `team` à
   *  `place`. Énumère 3^k (k = matchs restants, plafonné). Scores représentatifs : V = 2-0,
   *  N = 1-1, D = 0-2 ; la marge minimale est recherchée à part pour les cas au goal-average. */
  private solvePlacement(base: Base, team: string, place: number): PlacementSolve {
    const group = byCode.get(team)?.group ?? "";
    const groupTeams = groups[group] ?? [];
    const gm = schedule.filter((m) => m.stage === "group" && m.group === group);
    const played = gm
      .filter((m) => base.fixed[m.id])
      .map((m) => ({ home: m.home, away: m.away, score: base.fixed[m.id]!.score }));
    const remaining = gm.filter((m) => !base.fixed[m.id]);
    const elos = base.elos;
    const seed = `wc26-proj-${group}`;
    // Différence de buts ACTUELLE (matchs déjà joués) — l'avance/le retard à préserver.
    const curGd = (t: string): number =>
      played.reduce((g, r) => g + (r.home === t ? r.score[0] - r.score[1] : r.away === t ? r.score[1] - r.score[0] : 0), 0);
    const rankIdx = (results: { home: string; away: string; score: readonly [number, number] }[]): number =>
      rankGroup(groupTeams, results, { elos, drawSeed: seed }).findIndex((x) => x.team === team);

    if (!remaining.length) {
      return { team, place, group, settled: true, settledOk: rankIdx(played) + 1 === place, scenarios: [], tooMany: false };
    }
    if (remaining.length > 4) {
      return { team, place, group, settled: false, settledOk: false, scenarios: [], tooMany: true };
    }
    const OUT = [
      { tag: "H" as const, score: [2, 0] as [number, number] },
      { tag: "D" as const, score: [1, 1] as [number, number] },
      { tag: "A" as const, score: [0, 2] as [number, number] },
    ];
    const total = 3 ** remaining.length;
    const scenarios: CondScenario[] = [];
    for (let mask = 0; mask < total; mask++) {
      let m = mask;
      const hypo: { home: string; away: string; score: [number, number] }[] = [];
      const parts: string[] = [];
      for (const match of remaining) {
        const o = OUT[m % 3]!;
        m = Math.floor(m / 3);
        hypo.push({ home: match.home, away: match.away, score: o.score });
        parts.push(Projection.outcomeDesc(match.home, match.away, o.tag));
      }
      const ranked = rankGroup(groupTeams, [...played, ...hypo], { elos, drawSeed: seed });
      const idx = ranked.findIndex((x) => x.team === team);
      if (idx + 1 !== place) continue;
      const tiedWith = Projection.tiedNeighbor(ranked, idx);
      // On EXPLOITE toute l'info de départage : d'abord la confrontation directe (1ᵉʳ
      // critère FIFA) lue dans les résultats joués/projetés, puis (si nul) la différence
      // de buts ACTUELLE comme avance à préserver.
      let h2h: "win" | "draw" | "loss" | null = null;
      if (tiedWith) {
        const dm = [...played, ...hypo].find(
          (r) => (r.home === team && r.away === tiedWith) || (r.home === tiedWith && r.away === team),
        );
        if (dm) {
          const th = dm.home === team;
          const ts = th ? dm.score[0] : dm.score[1];
          const rs = th ? dm.score[1] : dm.score[0];
          h2h = ts > rs ? "win" : ts < rs ? "loss" : "draw";
        }
      }
      scenarios.push({ desc: parts.join(" · "), tiedWith, h2h, gdGap: tiedWith ? curGd(team) - curGd(tiedWith) : null });
    }
    return { team, place, group, settled: false, settledOk: false, scenarios, tooMany: false };
  }

  private solveSectionHtml(s: PlacementSolve): string {
    const head = `<div class="prj-cs-h">${t("proj.solve.head", { team: `${flagSpan(s.team)} <b>${esc(teamName(s.team))}`, place: `${placeLabels()[s.place]}</b>`, group: esc(s.group) })}</div>`;
    if (s.settled) {
      return `${head}<div class="prj-cs-note ${s.settledOk ? "ok" : "no"}">${s.settledOk ? t("proj.solve.settledOk") : t("proj.solve.settledNo")}</div>`;
    }
    if (s.tooMany) return `${head}<div class="prj-cs-note">${t("proj.solve.tooMany")}</div>`;
    if (!s.scenarios.length) return `${head}<div class="prj-cs-note no">${t("proj.solve.none")}</div>`;
    const shown = s.scenarios.slice(0, 16);
    const rows = shown
      .map((sc) => {
        let tag = "";
        if (sc.tiedWith) {
          const me = esc(teamName(s.team));
          const rival = esc(teamName(sc.tiedWith));
          let txt: string;
          if (sc.h2h === "win") {
            // 1ᵉʳ critère : la confrontation directe est déjà gagnée → tranché, point final.
            txt = t("proj.solve.h2hWin", { me, rival });
          } else if (sc.h2h === "loss") {
            txt = t("proj.solve.h2hLoss", { me, rival });
          } else {
            // confrontation directe NULLE (ou à venir, nulle dans ce combo) → différence de buts.
            const g = sc.gdGap ?? 0;
            const abs = Math.abs(g);
            const buts = abs > 1 ? t("proj.solve.goalOther", { n: abs }) : t("teff.main.goals", { n: abs });
            const lead =
              g > 0
                ? t("proj.solve.gdLead", { me, rival, buts })
                : g < 0
                  ? t("proj.solve.gdDeficit", { me, rival, buts })
                  : t("proj.solve.gdEqual", { me, rival });
            txt = t("proj.solve.h2hDraw", { lead });
          }
          tag = ` <span class="prj-cs-gd">${t("proj.solve.tiedWith", { rival, txt })}</span>`;
        }
        return `<li>${esc(sc.desc)}${tag}</li>`;
      })
      .join("");
    const more = s.scenarios.length > shown.length ? `<li class="prj-cs-more">${t("proj.solve.more", { n: s.scenarios.length - shown.length })}</li>` : "";
    return `${head}<ul class="prj-cs-list">${rows}${more}</ul>`;
  }

  private readonly onCondKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") this.closeCondModal();
  };

  private closeCondModal(): void {
    if (!this.condModal) return;
    document.removeEventListener("keydown", this.onCondKey);
    const ov = this.condModal;
    this.condModal = null;
    ov.classList.remove("open");
    setTimeout(() => ov.remove(), 250);
  }

  /** Place de groupe que `teamCode` doit prendre pour occuper le côté `side` du nœud
   *  `node` : on REMONTE le bracket jusqu'aux créneaux R32 qui alimentent ce côté, et on
   *  garde celui qui correspond au groupe de l'équipe (préférence au placement le plus
   *  probable dans le MC s'il est structurellement valide). 100 % dérivé des données. */
  private requiredPlacement(node: string, side: "a" | "b", teamCode: string): number | null {
    const group = byCode.get(teamCode)?.group;
    if (!group || !this.output) return null;
    const slots = r32SlotsFeeding(node, side, (id) => slotByMatch.get(id));
    const matches = slots.map((s) => placementForGroup(s, group)).filter((p): p is number => p != null);
    if (!matches.length) return null;
    const r32 = likeliestMatchByRound(this.output, teamCode).find((l) => l.round === "r32");
    if (r32?.matchId != null) {
      const mcNode = `M${r32.matchId}`;
      const mcSlots = slotByMatch.get(r32.matchId);
      const mcSide = teamSideAt(this.output, teamCode, mcNode);
      const mcPlace = placementOf(mcSide === "a" ? mcSlots?.home : mcSlots?.away);
      if (mcPlace != null && matches.includes(mcPlace)) return mcPlace; // le plus probable, si valide
    }
    return matches.includes(1) ? 1 : matches.includes(2) ? 2 : (matches[0] ?? null);
  }

  /** Section « qualif » de repli quand le placement exact n'est pas déterminable. */
  private qualFallbackHtml(teamCode: string): string {
    const g = byCode.get(teamCode)?.group ?? "?";
    return `<div class="prj-cs-h">${t("proj.qualFallback.head", { team: `${flagSpan(teamCode)} <b>${esc(teamName(teamCode))}</b>`, group: esc(g) })}</div>`;
  }

  /** Ouvre la modale des conditions d'un affrontement (spec = « depth:CODE »), à N'IMPORTE
   *  quel tour : place de groupe de CHAQUE équipe (remontée du bracket) + toutes les
   *  combinaisons de scores des matchs de groupe non joués qui la réalisent, puis (tours
   *  profonds) la chaîne de KO à franchir pour s'y croiser. Tout est dérivé du MC + des
   *  résultats courants — rien de figé. */
  private openCondModal(spec: string): void {
    this.closeCondModal();
    const [d, code, mid] = spec.split(":");
    const depth = Number(d);
    const matchId = Number(mid);
    // Le créneau (matchId) est porté par l'élément cliqué → on prend EXACTEMENT le nœud
    // affiché (cohérent avec l'onglet 16es sélectionné), pas un agrégat.
    if (!this.output || !code || !Number.isFinite(depth) || !Number.isFinite(matchId)) return;
    const base = this.getBase();
    const roundLabel = roundLabels()[depth] ?? "";
    const node = `M${matchId}`;
    const marSide = teamSideAt(this.output, this.team, node);
    const oppSide = marSide === "a" ? "b" : "a";
    const teamPlace = this.requiredPlacement(node, marSide, this.team);
    const oppPlace = this.requiredPlacement(node, oppSide, code);
    const teamSolve = teamPlace ? this.solvePlacement(base, this.team, teamPlace) : null;
    const oppSolve = oppPlace ? this.solvePlacement(base, code, oppPlace) : null;
    const teamSection = teamSolve ? this.solveSectionHtml(teamSolve) : this.qualFallbackHtml(this.team);
    const oppSection = oppSolve ? this.solveSectionHtml(oppSolve) : this.qualFallbackHtml(code);
    const intro =
      depth === 0
        ? t("proj.cond.introR32")
        : t("proj.cond.introDeep", { team: esc(teamName(this.team)), opp: esc(teamName(code)), round: esc(roundLabel) });
    const koNote =
      depth > 0
        ? `<div class="prj-cs-note">${t("proj.cond.koNote", { team: esc(teamName(this.team)), opp: esc(teamName(code)), rounds: esc(roundLabels().slice(0, depth).join(", ")), round: esc(roundLabel) })}</div>`
        : "";
    // Note de départage : visible seulement si une combinaison se joue à égalité de points
    // → explique pourquoi le score d'un match peut faire basculer le classement d'une autre.
    const hasTie = [teamSolve, oppSolve].some((s) => s?.scenarios.some((sc) => sc.tiedWith));
    const tieNote = hasTie
      ? `<div class="prj-cs-tie"><i class="ti ti-info-circle" aria-hidden="true"></i> ${t("proj.cond.tieNote")}</div>`
      : "";
    const body = `<p class="prj-cond-sub note">${intro}</p>${teamSection}${oppSection}${koNote}${tieNote}`;
    const ov = document.createElement("div");
    ov.className = "md-overlay prj-cond-ov";
    ov.innerHTML = `<div class="md-card prj-cond-card" role="dialog" aria-modal="true" aria-label="${t("proj.cond.dialogAria")}">
      <button class="md-close" aria-label="${t("common.close")}">×</button>
      <div class="prj-cond-h"><i class="ti ti-list-check" aria-hidden="true"></i> ${t("proj.cond.title", { team: `${flagSpan(this.team)} ${esc(teamName(this.team))}`, opp: `${flagSpan(code)} ${esc(teamName(code))}`, round: esc(roundLabel) })}</div>
      ${body}
    </div>`;
    this.el.appendChild(ov);
    this.condModal = ov;
    ov.style.zIndex = String(frontZ());
    void ov.offsetWidth;
    ov.classList.add("open");
    document.addEventListener("keydown", this.onCondKey);
    ov.addEventListener("click", (e) => {
      if (e.target === ov) this.closeCondModal();
    });
    ov.querySelector<HTMLButtonElement>(".md-close")?.addEventListener("click", () => this.closeCondModal());
  }

  /** Remplace le bouton d'appel par sa version à jour (sans toucher au reste). */
  private refreshCta(): void {
    const cur = this.el.querySelector(".prj-cta");
    const tmp = document.createElement("div");
    tmp.innerHTML = this.ctaHtml();
    const next = tmp.firstElementChild;
    if (cur && next) cur.replaceWith(next);
    else if (cur && !next) cur.remove();
    this.wireCta();
  }

  private wireModal(ov: HTMLElement): void {
    ov.querySelectorAll<HTMLInputElement>(".prj-sc").forEach((inp) => {
      inp.onchange = () => {
        const row = inp.closest<HTMLElement>(".prj-wm");
        if (!row) return;
        const mid = Number(row.dataset.mid);
        const h = row.querySelector<HTMLInputElement>('.prj-sc[data-side="h"]')?.value ?? "";
        const a = row.querySelector<HTMLInputElement>('.prj-sc[data-side="a"]')?.value ?? "";
        if (h === "" && a === "") this.scores.delete(mid);
        else this.scores.set(mid, [h, a]);
        this.refreshModalStandings();
        if (this.recalcTimer) clearTimeout(this.recalcTimer);
        this.recalcTimer = setTimeout(() => void this.runQuiet(), 350);
      };
    });
    ov.addEventListener("click", (e) => {
      if (e.target === ov) this.closeModal(false); // clic sur le fond = abandon
    });
    ov.querySelector<HTMLButtonElement>(".md-close")?.addEventListener("click", () => this.closeModal(false));
    ov.querySelector<HTMLButtonElement>(".prj-done")?.addEventListener("click", () => this.closeModal(true));
    ov.querySelector<HTMLButtonElement>(".prj-reset")?.addEventListener("click", () => {
      this.scores.clear();
      this.openModal(); // ré-affiche le tour avec les scores prédits
      if (this.recalcTimer) clearTimeout(this.recalcTimer);
      this.recalcTimer = setTimeout(() => void this.runQuiet(), 50);
    });
  }

  private wire(): void {
    const teamSel = this.el.querySelector<HTMLSelectElement>(".prj-team");
    if (teamSel)
      teamSel.onchange = () => {
        this.closeModalNow();
        this.closeCondModal();
        this.team = teamSel.value;
        this.wizardStep = 0;
        this.roundDone = false;
        this.tabSel = {}; // les onglets d'arbre sont propres à l'équipe
        this.render();
      };
    const formCb = this.el.querySelector<HTMLInputElement>(".prj-form-cb");
    if (formCb)
      formCb.onchange = () => {
        this.formOn = formCb.checked; // recalcul forcé (la clé MC inclut formOn)
        void this.run();
      };
    const absCb = this.el.querySelector<HTMLInputElement>(".prj-abs-cb");
    if (absCb)
      absCb.onchange = () => {
        this.absenceOn = absCb.checked; // recalcul forcé (la clé MC inclut absenceOn)
        void this.run();
      };
    this.wireCta();
  }

  private wireCta(): void {
    const cta = this.el.querySelector<HTMLButtonElement>(".prj-cta");
    if (cta) cta.onclick = () => this.onCta();
  }

  private onCta(): void {
    const last = this.wizardSteps().length - 1;
    if (this.roundDone && this.wizardStep < last) {
      this.wizardStep++; // « passer au prochain tour »
      this.roundDone = false;
    } else if (this.roundDone && this.wizardStep >= last) {
      this.wizardStep = 0; // tous les tours faits → revue depuis le début
      this.roundDone = false;
    }
    this.everOpened = true;
    this.openModal();
  }
}
